<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  tickets: { type: Array, default: () => [] },
  disabled: { type: Boolean, default: false },
  allowId: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue', 'confirm']);
const { t } = useI18n();
const PAGE_SIZE = 8;
const limit = ref(PAGE_SIZE);
const matches = computed(() => {
  const query = props.modelValue.trim().toLocaleLowerCase();
  return props.tickets.filter(ticket =>
    [ticket.chamado_id, ticket.ticket_code, ticket.customer_name].some(value =>
      String(value || '')
        .toLocaleLowerCase()
        .includes(query)
    )
  );
});
watch(
  () => props.modelValue,
  () => {
    limit.value = PAGE_SIZE;
  }
);
</script>

<template>
  <div class="flex flex-col gap-2">
    <Input
      :model-value="modelValue"
      :label="
        t(
          allowId
            ? 'GG_FOLLOWUP.SUPPORT.ID_SEARCH'
            : 'GG_FOLLOWUP.SUPPORT.SEARCH'
        )
      "
      :placeholder="t('GG_FOLLOWUP.SUPPORT.PLACEHOLDER')"
      :disabled="disabled"
      autocomplete="off"
      @update:model-value="emit('update:modelValue', $event)"
      @keydown.enter="emit('confirm')"
    />
    <p class="m-0 text-xs text-n-slate-11">
      {{
        t(
          allowId
            ? 'GG_FOLLOWUP.SUPPORT.EXISTING_HINT'
            : 'GG_FOLLOWUP.SUPPORT.LOCAL_HINT'
        )
      }}
    </p>
    <div v-if="matches.length" class="flex flex-col gap-1">
      <p class="m-0 text-xs font-medium text-n-slate-12">
        {{ t('GG_FOLLOWUP.SUPPORT.LINKED') }}
      </p>
      <ul
        class="m-0 p-1 list-none max-h-52 overflow-y-auto rounded-lg border border-n-weak"
      >
        <li v-for="ticket in matches.slice(0, limit)" :key="ticket.chamado_id">
          <button
            type="button"
            :disabled="disabled"
            class="w-full flex flex-col gap-1 rounded-md p-2 text-start hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            @click="emit('update:modelValue', String(ticket.chamado_id))"
          >
            <span class="text-sm font-medium text-n-slate-12">
              {{ ticket.ticket_code }} ·
              {{ t('GG_FOLLOWUP.SUPPORT.ID', { id: ticket.chamado_id }) }}
            </span>
            <span
              v-if="ticket.customer_name"
              class="text-xs text-n-slate-11 break-words"
            >
              {{ ticket.customer_name }}
            </span>
          </button>
        </li>
      </ul>
      <Button
        v-if="matches.length > limit"
        :label="t('GG_FOLLOWUP.SUPPORT.MORE')"
        variant="ghost"
        color="slate"
        size="sm"
        @click="limit += PAGE_SIZE"
      />
    </div>
    <p v-else-if="!allowId" role="status" class="m-0 text-xs text-n-slate-11">
      {{
        t(
          modelValue
            ? 'GG_FOLLOWUP.SUPPORT.NOT_FOUND'
            : 'GG_FOLLOWUP.SUPPORT.EMPTY'
        )
      }}
    </p>
  </div>
</template>
